import {FormEvent, useState} from 'react';
import {Search} from 'lucide-react';
import {PhoneHQButton, PhoneHQInput, Segmented, StatusBadge} from 'ch3-ui-lib';
import {useCheckNumberMutation} from '../../store/features/CallRestrictions/callRestrictionsService';
import type {CheckResult, RulePurpose} from '../../store/features/CallRestrictions/callRestrictionsTypes';
import {readApiError} from '../../utils/callRestrictions/apiErrors';
import {digitsOf} from '../../utils/callRestrictions/destinations';
import {checkText} from './checkText';
import {Card, LabeledSelect, lucide} from './parts';

export interface CallerOption {
    userId: number;
    name: string;
    firstName: string;
}

export interface FromNumberOption {
    msisdn: string;
    label?: string;
}

interface Checked {
    result: CheckResult;
    purpose: RulePurpose;
    who?: string;
}

/** "Check a number": POST /checks and say which rule decides. */
export const CheckNumberCard = ({callers, lines = []}: {callers: CallerOption[]; lines?: FromNumberOption[]}) => {
    const [number, setNumber] = useState('');
    const [userId, setUserId] = useState('');
    const [fromNumber, setFromNumber] = useState('');
    const [purpose, setPurpose] = useState<RulePurpose>('CALL');
    const [checked, setChecked] = useState<Checked>();
    const [error, setError] = useState<string>();
    const [check, {isLoading}] = useCheckNumberMutation();

    const submit = async (event: FormEvent) => {
        event.preventDefault();
        const digits = digitsOf(number);
        if (digits.length < 3) {
            setError('Enter a number to check.');
            return;
        }
        setError(undefined);
        try {
            // Keep the leading + when typed; the backend normalises with the tenant's country otherwise.
            const typed = number.trim().startsWith('+') ? `+${digits}` : digits;
            const result = await check({number: typed, purpose, ...(userId ? {userId: Number(userId)} : {}), ...(fromNumber ? {fromNumber} : {})}).unwrap();
            setChecked({result, purpose, who: callers.find((c) => String(c.userId) === userId)?.firstName});
        } catch (e) {
            setChecked(undefined);
            setError(readApiError(e)?.message ?? 'Couldn’t check this number. Try again.');
        }
    };

    const text = checked && checkText(checked.result, checked.purpose, checked.who);

    return (
        <Card title="Check a number" description="See whether a call or forward would go through, and which rule decides." testId="check-number-card">
            <form onSubmit={submit} className="flex flex-col gap-3">
                <div className="flex flex-wrap items-end gap-2">
                    <div className="min-w-[220px] flex-[1_1_260px]">
                        <PhoneHQInput label="Number" inputMode="tel" placeholder="+48 22 100 20 30" value={number}
                                      prefixIcon={<Search size={16} aria-hidden="true" className={lucide}/>}
                                      onChange={(e) => setNumber(e.target.value)}/>
                    </div>
                    <LabeledSelect label="Who is calling" value={userId} onChange={(e) => setUserId(e.target.value)} className="min-w-[200px] flex-[1_1_220px]">
                        <option value="">Anyone (company rules)</option>
                        {callers.map((c) => <option key={c.userId} value={c.userId}>{c.name}</option>)}
                    </LabeledSelect>
                    {lines.length > 0 && (
                        <LabeledSelect label={purpose === 'FORWARD' ? 'Forwarded line' : 'From number'} value={fromNumber} onChange={(e) => setFromNumber(e.target.value)} className="min-w-[200px] flex-[1_1_220px]">
                            <option value="">Any number</option>
                            {lines.map((line) => <option key={line.msisdn} value={line.msisdn}>{line.label ? `+${line.msisdn} · ${line.label}` : `+${line.msisdn}`}</option>)}
                        </LabeledSelect>
                    )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Segmented<RulePurpose>
                        label="Check for"
                        value={purpose}
                        onChange={setPurpose}
                        options={[{id: 'CALL', label: 'Outgoing call'}, {id: 'FORWARD', label: 'Forwarding'}]}
                    />
                    <PhoneHQButton type="submit" variant="secondary" disabled={isLoading} data-testid="check-number-submit">Check</PhoneHQButton>
                </div>
                {error && <p role="alert" className="m-0 text-footnote text-danger-ink">{error}</p>}
                {text && (
                    <div role="status" data-testid="check-number-result" className="flex flex-wrap items-start gap-3 rounded-control border border-solid border-line p-3">
                        <StatusBadge tone={text.verdict === 'Allowed' ? 'success' : 'danger'} className="!h-7 !px-2.5 !text-callout !font-semibold">
                            {text.verdict}
                        </StatusBadge>
                        <div className="flex min-w-0 flex-[1_1_300px] flex-col gap-1">
                            <div className="text-callout text-ink">{text.rule}</div>
                            <div className="text-footnote text-ink-muted">{text.sees}</div>
                            {checked?.result.fromNumber && (
                                <div className="text-footnote text-ink-muted">{checked.purpose === 'FORWARD' ? 'Forwarded line' : 'From'} +{digitsOf(checked.result.fromNumber)}</div>
                            )}
                        </div>
                    </div>
                )}
            </form>
        </Card>
    );
};
